<template>
  <div
    :id="String(el.id)"
    ref="articlesList"
    :key="el.id"
    :data-index="index"
    :class="['articles-list__el', `articles-list__el--${variant}`, { 'articles-list__el--no-reverse': noReverse }]"
  >
    <div class="articles-list-img-container">
      <AppLink :to="routerTo">
        <img
          class="articles-list-img"
          :src="photoUrl"
          @click="useBlock(index, el.id)"
        />
      </AppLink>
    </div>

    <div class="articles-list-content">
      <div
        v-if="isUgc"
        class="articles-list-reader-experience"
      >
        Опыт читателя
      </div>

      <AppLink :to="articleLink">
        <span
          class="articles-list-title"
          @click="useBlock(index, el)"
          v-html="el.title"
        />&nbsp;
        <span
          class="articles-list-subtitle"
          @click="useBlock(index, el)"
          v-html="el.tagline"
        />

        <div class="articles-list-time">
          <div class="articles-list-time__reading-time">
            <span class="articles-list-time__reading-time-icon">
              <IconBase
                name="icon-clock"
                color="--alias-color-icon-subtle"
              />
            </span>
            <span
              class="articles-list-time__reading-time-text"
              v-html="minutesStr(el.readingTime)"
            />
          </div>
          <span class="articles-list-dot">·</span>
          <time class="articles-list-time-date">{{ getDate(el.publishedAt) }}</time>
        </div>
      </AppLink>
      <div class="articles-list-hashtags">
        <AppLink
          v-for="hashtag in hashtags(el.geo)"
          :key="hashtag.slug"
          class="articles-list__hashtags-el"
          :to="goTo(hashtag.slug)"
          >#{{ hashtag.name }}
        </AppLink>
        <AppLink
          v-if="isUgc"
          to="/journal/ugc/"
          class="articles-list__hashtags-el"
        >
          #Опыт читателя
        </AppLink>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed } from 'vue';

  import { minutesStr, getDate } from '@/shared/helpers/date';
  import type { Article, Geo } from '@/shared/models/journal';
  import { IconBase } from '@/shared/ui/iconBase';

  const props = withDefaults(
    defineProps<{
      el: Article;
      index: number;
      useBlock: Function;
      routerTo: string;
      variant?: 'default' | 'slide';
      /** Не применять row-reverse на мобильных */
      noReverse?: boolean;
    }>(),
    {
      variant: 'default',
      noReverse: false,
    },
  );

  const goTo = (slug: string) => `/journal/country/articles/${slug}/`;

  const isUgc = computed(() => props.el?.isUgc);

  const articleLink = computed(() => `/journal/articles/${props.el.slug}/`);

  const photoUrl = computed(() => {
    // Приоритет: mainPhoto.thumbnail_xl -> mainPhoto.lightbox -> lightboxPhotoUrl (fallback)
    return props.el.mainPhoto?.thumbnail_xl || props.el.mainPhoto?.lightbox || props.el.lightboxPhotoUrl || '';
  });

  const hashtags = (geo?: Geo) => geo?.country?.slice(0, 3) || [];
</script>

<style lang="scss" scoped>
  a {
    color: var(--alias-color-link-default);
    text-decoration: none;

    &:hover {
      color: var(--alias-color-link-default-hovered);
    }
  }

  .articles-list__el {
    position: relative;
    cursor: pointer;

    &--slide {
      @include mobile {
        flex-direction: column;

        .articles-list-reader-experience {
          position: absolute;
          width: auto;
          padding: 8px 24px;
          border-radius: 16px 0;

          @include base-300-300-semi;
        }
      }
    }

    &--no-reverse {
      @include mobile {
        flex-direction: column !important;
      }
    }

    @include tablet {
      width: 100%;
    }

    @include mobile {
      display: flex;
      flex-direction: row-reverse;
      width: 100%;
    }
  }

  .articles-list-img-container:hover img {
    transform: scale(1.1);
  }

  .articles-list-img-container {
    overflow: hidden;
    width: 100%;
    height: 287px;
    border-radius: 16px;

    .articles-list__el--slide & {
      height: 280px;

      @include tablet {
        height: 211px;
      }

      @include mobile {
        max-width: 100%;
        height: 211px;
        margin-left: 0;
        border-radius: 16px;
      }
    }

    @include tablet {
      height: 164px;
    }

    @include mobile {
      flex-shrink: 0;
      max-width: 72px;
      height: 96px;
      margin-left: 16px;
      border-radius: 8px;
    }
  }

  .articles-list-img {
    object-fit: cover;
    width: 100%;
    height: 100%;
    transition: transform 0.7s ease;
  }

  .articles-list-content {
    margin-top: 16px;
    font-size: 17px;
    line-height: 24px;

    .articles-list__el--slide & {
      @include mobile {
        margin-top: 8px;
      }
    }

    @include tablet {
      width: 100%;
      margin-top: 8px;
    }

    @include mobile {
      margin-top: 0;
    }
  }

  .articles-list-hashtags {
    display: flex;
    flex-wrap: wrap;
    margin-top: 8px;
    line-height: 24px;

    @include mobile {
      margin-top: 4px;
    }
  }

  .articles-list-hashtags-el {
    margin-right: 8px;
    text-decoration: none;

    @include base-300-300-regular;
  }

  .articles-list-dot {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 4px;
    color: var(--alias-color-text-subtle);
    font-size: 14px;
  }

  .articles-list-title {
    margin-top: 16px;
    color: var(--alias-color-text-default);
    font-weight: 900;
  }

  .articles-list-time {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-top: 8px;
    color: var(--alias-color-text-subtle);
    font-size: 14px;

    @include mobile {
      margin-top: 4px;
    }
  }

  .articles-list-time__reading-time {
    display: flex;
    gap: 0 4px;
  }

  .articles-list-time__reading-time-icon {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .articles-list-time__reading-time-icon .icon {
    fill: var(--alias-color-icon-subtle);
  }

  .articles-list-time__reading-time-text {
    display: flex;
    align-items: center;
  }

  .articles__hashtags-el,
  .articles-list__hashtags-el {
    margin-right: 8px;
    font-size: 14px;
    text-decoration: none;
  }

  .articles-list-subtitle {
    color: var(--alias-color-text-default);
    word-wrap: break-word;
    hyphens: auto;
  }

  .articles-list-reader-experience {
    position: absolute;
    top: 0;
    left: 0;
    padding: 8px 24px;
    border-radius: 16px 0;
    background-color: var(--alias-color-background-accent-orange);
    color: var(--alias-color-text-inverse);

    @include base-300-300-semi;

    @include tablet {
      padding: 6px 12px;

      @include base-200-200-medium;
    }

    @include mobile {
      position: relative;
      max-width: fit-content;
      margin-bottom: 4px;
      padding: 6px 8px;
      border-radius: 6px;
    }
  }
</style>
